<template>
  <section class="lease-cars-section">
    <div class="container">
      <!-- Main Header -->
      <div
        class="section-header"
        data-aos="zoom-in"
        data-aos-duration="800"
        data-aos-delay="100"
      >
        <h5 class="pre-title" data-aos="fade-down" data-aos-delay="200">
          Flexible Leasing Options
        </h5>
        <h1 class="section-title" data-aos="fade-up" data-aos-delay="300">
          Cars Available for Lease
        </h1>
        <div
          class="title-underline"
          data-aos="slide-right"
          data-aos-delay="400"
        ></div>
        <p class="section-tagline" data-aos="fade-up" data-aos-delay="500">
          Find your perfect lease vehicle with flexible terms and competitive
          rates
        </p>
      </div>
      <CarRequestForm @request-submitted="handleRequestSubmitted" />

      <div class="cars-container">
        <!-- Filter Sidebar -->
        <div class="filters-sidebar" :class="{ active: showMobileFilters }">
          <div class="filters-header">
            <h3>Filters</h3>
            <button class="close-filters" @click="toggleMobileFilters">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class="filters-content">
            <!-- Search Box -->
            <div class="filter-group">
              <h4>Search</h4>
              <div class="search-box">
                <input
                  type="text"
                  v-model="tempFilters.searchQuery"
                  placeholder="Search by model or manufacturer"
                />
                <svg
                  class="search-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
            </div>

            <!-- Manufacturer Filter -->
            <div class="filter-group">
              <h4>Manufacturer</h4>
              <div class="filter-options">
                <label
                  v-for="manufacturer in manufacturers"
                  :key="manufacturer"
                  class="filter-checkbox"
                >
                  <input
                    type="checkbox"
                    :value="manufacturer"
                    v-model="tempFilters.selectedManufacturers"
                  />
                  <span class="checkmark"></span>
                  {{ manufacturer }}
                </label>
              </div>
            </div>

            <!-- Engine Type Filter -->
            <div class="filter-group">
              <h4>Engine Type</h4>
              <div class="filter-options">
                <label
                  v-for="engine in engineTypes"
                  :key="engine"
                  class="filter-checkbox"
                >
                  <input
                    type="checkbox"
                    :value="engine"
                    v-model="tempFilters.selectedEngineTypes"
                  />
                  <span class="checkmark"></span>
                  {{ engine }}
                </label>
              </div>
            </div>

            <!-- Year Range Filter -->
            <div class="filter-group">
              <h4>Year Range</h4>
              <div class="range-filter">
                <div class="range-inputs">
                  <select v-model.number="tempFilters.yearMin">
                    <option
                      v-for="year in yearOptions"
                      :key="`min-${year}`"
                      :value="year"
                    >
                      {{ year }}
                    </option>
                  </select>
                  <span>to</span>
                  <select v-model.number="tempFilters.yearMax">
                    <option
                      v-for="year in yearOptions"
                      :key="`max-${year}`"
                      :value="year"
                    >
                      {{ year }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Downpayment Lease Price Range Filter -->
            <div class="filter-group">
              <h4>Downpayment</h4>
              <div class="range-filter">
                <div class="price-range">
                  <span
                    >${{ (tempFilters.priceMin || 0).toLocaleString() }}</span
                  >
                  <span class="price-middle">-</span>
                  <span
                    >${{ (tempFilters.priceMax || 500).toLocaleString() }}</span
                  >
                </div>
                <div class="slider-container">
                  <input
                    type="range"
                    v-model.number="tempFilters.priceMin"
                    :min="priceMinLimit"
                    :max="priceMaxLimit"
                    class="price-slider min-slider"
                  />
                  <input
                    type="range"
                    v-model.number="tempFilters.priceMax"
                    :min="priceMinLimit"
                    :max="priceMaxLimit"
                    class="price-slider max-slider"
                  />
                </div>
              </div>
            </div>

            <!-- Filter Action Buttons -->
            <div class="filter-actions">
              <button class="apply-filters" @click="applyFilters">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20,6 9,17 4,12"></polyline>
                </svg>
                Apply Filters
              </button>

              <button class="reset-filters" @click="resetFilters">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
                  ></path>
                  <path d="M3 3v5h5"></path>
                </svg>
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="cars-content">
          <!-- Mobile Filter Toggle -->
          <button class="mobile-filter-toggle" @click="toggleMobileFilters">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="4" y1="6" x2="20" y2="6"></line>
              <line x1="4" y1="12" x2="20" y2="12"></line>
              <line x1="4" y1="18" x2="20" y2="18"></line>
            </svg>
            Filters
          </button>

          <!-- Results Summary -->
          <div class="results-summary">
            <p>
              Showing {{ filteredCars.length }} of {{ cars.length }} lease
              vehicles
            </p>
          </div>

          <!-- Cars Grid -->
          <div
            class="car-results"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="600"
          >
            <div
              v-for="car in filteredCars"
              :key="car.id"
              class="car-card"
              @click="goToCarPage(car.id)"
            >
              <div class="car-image-wrapper">
                <img
                  :src="car.image"
                  :alt="`${car.manufacturer} ${car.model}`"
                  class="car-image"
                />
                <span class="lease-badge">For Lease</span>
              </div>
              <div class="car-card-content">
                <h3 class="car-name">
                  {{ car.manufacturer }} {{ car.model }} {{ car.year }}
                </h3>
                <div class="lease-pricing">
                  <div class="price-option" v-if="car.lease_price_per_day">
                    <span class="price-label">Downpayment</span>
                    <span class="price-value"
                      >${{ car.lease_price_per_day.toLocaleString() }}</span
                    >
                  </div>
                  <div class="price-option" v-if="car.lease_price_per_month">
                    <span class="price-label">Monthly</span>
                    <span class="price-value"
                      >${{ car.lease_price_per_month.toLocaleString() }}</span
                    >
                  </div>
                </div>
                <p class="car-details">
                  {{
                    car.mileage_miles
                      ? car.mileage_miles.toLocaleString() + " miles"
                      : "Mileage N/A"
                  }}
                  • {{ car.engine_type }}
                </p>
                <div class="lease-info" v-if="car.minimum_lease_days">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12,6 12,12 16,14"></polyline>
                  </svg>
                  Min. {{ car.minimum_lease_days }} month{{
                    car.minimum_lease_days > 1 ? "s" : ""
                  }}
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="loading-container">
            <svg
              class="spinner"
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </div>

          <!-- No Cars State -->
          <div
            v-if="!isLoading && filteredCars.length === 0"
            class="no-results"
          >
            <div class="no-results-container">
              <svg
                class="no-results-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M21 11.5a8.38 8.38 0 0 1-11.5 7.5 8.38 8.38 0 0 1-7.5-11.5A8.38 8.38 0 0 1 13.5 3a8.38 8.38 0 0 1 7.5 8.5z"
                />
              </svg>
              <h3 class="no-results-title">No Lease Cars Available</h3>
              <p class="no-results-text">
                No lease vehicles match your current filter criteria. Try
                adjusting your filters and click "Apply Filters".
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";
import axios from "axios";
import API_CONFIG from "@/config/api.config.js";
import CarRequestForm from "@/components/CarRequestForm.vue";

export default {
  name: "LeaseCars",
  components: {
    CarRequestForm,
  },
  data() {
    return {
      cars: [],
      filteredCars: [],
      isLoading: false,
      baseUrl: API_CONFIG.baseUrl,

      activeFilters: {
        searchQuery: "",
        selectedManufacturers: [],
        selectedEngineTypes: [],
        yearMin: 1990,
        yearMax: new Date().getFullYear(),
        priceMin: 0,
        priceMax: 500,
      },

      tempFilters: {
        searchQuery: "",
        selectedManufacturers: [],
        selectedEngineTypes: [],
        yearMin: 1990,
        yearMax: new Date().getFullYear(),
        priceMin: 0,
        priceMax: 500,
      },

      priceMinLimit: 0,
      priceMaxLimit: 500,
      showMobileFilters: false,
      initialLoadComplete: false,
    };
  },
  mounted() {
    AOS.init({
      once: false,
      offset: 50,
    });
    this.fetchLeaseCars();
  },
  beforeUnmount() {
    this.saveFiltersToSession();
  },
  computed: {
    manufacturers() {
      const uniqueManufacturers = [
        ...new Set(this.cars.map((car) => car.manufacturer)),
      ];
      return uniqueManufacturers.sort();
    },
    engineTypes() {
      const uniqueEngineTypes = [
        ...new Set(this.cars.map((car) => car.engine_type)),
      ];
      return uniqueEngineTypes.sort();
    },
    yearOptions() {
      const years = [];
      const currentYear = new Date().getFullYear();
      const minYear = Math.min(...this.cars.map((car) => car.year)) || 1990;

      for (let year = minYear; year <= currentYear; year++) {
        years.push(year);
      }
      return years;
    },
  },
  methods: {
    async fetchLeaseCars() {
      this.isLoading = true;
      try {
        const response = await axios.get(
          `${this.baseUrl}/api/cars/lease/search`
        );
        if (response.data.success) {
          this.cars = response.data.cars.map((car) => ({
            ...car,
            image: `${this.baseUrl}${car.image}`,
          }));

          if (this.cars.length > 0) {
            const DownpaymentPrices = this.cars
              .map((car) => car.lease_price_per_day)
              .filter((price) => price !== null && price !== undefined);

            if (DownpaymentPrices.length > 0) {
              this.priceMinLimit = Math.floor(Math.min(...DownpaymentPrices));
              this.priceMaxLimit = Math.ceil(Math.max(...DownpaymentPrices));
            }

            const years = this.cars.map((car) => car.year);
            const minYear = Math.min(...years);
            const maxYear = Math.max(...years);

            const defaultFilters = {
              searchQuery: "",
              selectedManufacturers: [],
              selectedEngineTypes: [],
              yearMin: minYear,
              yearMax: maxYear,
              priceMin: this.priceMinLimit,
              priceMax: this.priceMaxLimit,
            };

            const filtersFromUrl = this.getFiltersFromUrl(defaultFilters);
            const filtersFromSession =
              this.loadFiltersFromSession(defaultFilters);

            const initialFilters =
              filtersFromUrl || filtersFromSession || defaultFilters;

            this.activeFilters = { ...initialFilters };
            this.tempFilters = { ...initialFilters };

            this.initialLoadComplete = true;
          }

          this.performFiltering();
        }
      } catch (error) {
        console.error("Error fetching lease cars:", error);
      } finally {
        this.isLoading = false;
      }
    },

    getFiltersFromUrl(defaultFilters) {
      const params = new URLSearchParams(window.location.search);

      if (!params.toString()) {
        return null;
      }

      const filters = { ...defaultFilters };

      if (params.has("search")) {
        filters.searchQuery = params.get("search");
      }

      if (params.has("manufacturers")) {
        filters.selectedManufacturers = params
          .get("manufacturers")
          .split(",")
          .filter(Boolean);
      }

      if (params.has("engines")) {
        filters.selectedEngineTypes = params
          .get("engines")
          .split(",")
          .filter(Boolean);
      }

      if (params.has("yearMin")) {
        const yearMin = parseInt(params.get("yearMin"));
        if (!isNaN(yearMin)) {
          filters.yearMin = yearMin;
        }
      }

      if (params.has("yearMax")) {
        const yearMax = parseInt(params.get("yearMax"));
        if (!isNaN(yearMax)) {
          filters.yearMax = yearMax;
        }
      }

      if (params.has("priceMin")) {
        const priceMin = parseFloat(params.get("priceMin"));
        if (!isNaN(priceMin)) {
          filters.priceMin = priceMin;
        }
      }

      if (params.has("priceMax")) {
        const priceMax = parseFloat(params.get("priceMax"));
        if (!isNaN(priceMax)) {
          filters.priceMax = priceMax;
        }
      }

      return filters;
    },

    loadFiltersFromSession(defaultFilters) {
      try {
        const savedFilters = sessionStorage.getItem("leaseCarFilters");
        if (savedFilters) {
          const parsed = JSON.parse(savedFilters);
          return {
            ...defaultFilters,
            ...parsed,
          };
        }
      } catch (error) {
        console.error("Error loading filters from session:", error);
      }
      return null;
    },

    saveFiltersToSession() {
      try {
        const years = this.cars.map((car) => car.year);
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);

        const hasActiveFilters =
          this.activeFilters.searchQuery !== "" ||
          this.activeFilters.selectedManufacturers.length > 0 ||
          this.activeFilters.selectedEngineTypes.length > 0 ||
          this.activeFilters.yearMin !== minYear ||
          this.activeFilters.yearMax !== maxYear ||
          this.activeFilters.priceMin !== this.priceMinLimit ||
          this.activeFilters.priceMax !== this.priceMaxLimit;

        if (hasActiveFilters) {
          sessionStorage.setItem(
            "leaseCarFilters",
            JSON.stringify(this.activeFilters)
          );
        } else {
          sessionStorage.removeItem("leaseCarFilters");
        }
      } catch (error) {
        console.error("Error saving filters to session:", error);
      }
    },

    updateUrlWithFilters() {
      if (!this.initialLoadComplete) return;

      const params = new URLSearchParams();

      if (this.activeFilters.searchQuery) {
        params.set("search", this.activeFilters.searchQuery);
      }

      if (this.activeFilters.selectedManufacturers.length > 0) {
        params.set(
          "manufacturers",
          this.activeFilters.selectedManufacturers.join(",")
        );
      }

      if (this.activeFilters.selectedEngineTypes.length > 0) {
        params.set("engines", this.activeFilters.selectedEngineTypes.join(","));
      }

      const years = this.cars.map((car) => car.year);
      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);

      if (this.activeFilters.yearMin !== minYear) {
        params.set("yearMin", this.activeFilters.yearMin);
      }

      if (this.activeFilters.yearMax !== maxYear) {
        params.set("yearMax", this.activeFilters.yearMax);
      }

      if (this.activeFilters.priceMin !== this.priceMinLimit) {
        params.set("priceMin", this.activeFilters.priceMin);
      }

      if (this.activeFilters.priceMax !== this.priceMaxLimit) {
        params.set("priceMax", this.activeFilters.priceMax);
      }

      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

      window.history.replaceState({}, "", newUrl);
    },

    goToCarPage(carId) {
      this.$router.push({ path: `/cars/${carId}`, hash: "#top" }).then(() => {
        window.scrollTo(0, 0);
      });
    },

    applyFilters() {
      this.activeFilters = {
        searchQuery: this.tempFilters.searchQuery,
        selectedManufacturers: [...this.tempFilters.selectedManufacturers],
        selectedEngineTypes: [...this.tempFilters.selectedEngineTypes],
        yearMin: this.tempFilters.yearMin,
        yearMax: this.tempFilters.yearMax,
        priceMin: this.tempFilters.priceMin || 0,
        priceMax: this.tempFilters.priceMax || 500,
      };

      this.saveFiltersToSession();
      this.updateUrlWithFilters();
      this.performFiltering();

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      if (this.showMobileFilters) {
        this.toggleMobileFilters();
      }
    },

    performFiltering() {
      this.filteredCars = this.cars.filter((car) => {
        const searchMatch =
          this.activeFilters.searchQuery === "" ||
          car.manufacturer
            .toLowerCase()
            .includes(this.activeFilters.searchQuery.toLowerCase()) ||
          car.model
            .toLowerCase()
            .includes(this.activeFilters.searchQuery.toLowerCase());

        const manufacturerMatch =
          this.activeFilters.selectedManufacturers.length === 0 ||
          this.activeFilters.selectedManufacturers.includes(car.manufacturer);

        const engineMatch =
          this.activeFilters.selectedEngineTypes.length === 0 ||
          this.activeFilters.selectedEngineTypes.includes(car.engine_type);

        const yearMatch =
          car.year >= this.activeFilters.yearMin &&
          car.year <= this.activeFilters.yearMax;

        const DownpaymentPrice = car.lease_price_per_day || 0;
        const priceMatch =
          DownpaymentPrice >= (this.activeFilters.priceMin || 0) &&
          DownpaymentPrice <= (this.activeFilters.priceMax || Infinity);

        return (
          searchMatch &&
          manufacturerMatch &&
          engineMatch &&
          yearMatch &&
          priceMatch
        );
      });
    },

    resetFilters() {
      if (this.cars.length > 0) {
        const years = this.cars.map((car) => car.year);
        const resetFilters = {
          searchQuery: "",
          selectedManufacturers: [],
          selectedEngineTypes: [],
          yearMin: Math.min(...years),
          yearMax: Math.max(...years),
          priceMin: this.priceMinLimit,
          priceMax: this.priceMaxLimit,
        };

        this.tempFilters = { ...resetFilters };
        this.activeFilters = { ...resetFilters };

        sessionStorage.removeItem("leaseCarFilters");
        window.history.replaceState({}, "", window.location.pathname);

        this.performFiltering();
      }
    },

    toggleMobileFilters() {
      this.showMobileFilters = !this.showMobileFilters;

      if (this.showMobileFilters) {
        document.body.classList.add("filters-open");
      } else {
        document.body.classList.remove("filters-open");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.lease-cars-section {
  position: relative;
  padding: 100px 0;
  overflow: hidden;
  color: #1a1a2e;
  background-color: #ffffff;

  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
  }

  .section-header {
    text-align: center;
    margin-bottom: 3rem;

    .pre-title {
      text-transform: uppercase;
      letter-spacing: 3px;
      font-size: 0.9rem;
      font-weight: 500;
      color: #ee800a;
      margin-bottom: 1rem;
    }

    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      margin-bottom: 1rem;
      color: #09283f;
      letter-spacing: -0.5px;
      line-height: 1.1;
    }

    .title-underline {
      width: 80px;
      height: 3px;
      background: linear-gradient(to right, #ee800a, #ff7043);
      margin: 0 auto 1.5rem;
      border-radius: 2px;
    }

    .section-tagline {
      font-size: 1.3rem;
      font-weight: 300;
      line-height: 1.6;
      max-width: 700px;
      margin: 0 auto;
      color: #4a4a4a;
    }
  }

  .cars-container {
    display: flex;
    gap: 2rem;
    position: relative;
    align-items: flex-start;
  }

  .filters-sidebar {
    width: 280px;
    flex-shrink: 0;
    background: #f9f9f9;
    padding: 1.5rem;
    position: sticky;
    top: 100px;
    overflow-y: auto;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease;
    max-height: calc(100vh - 120px);
    align-self: flex-start;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #ccc;
      border-radius: 20px;
    }

    .filters-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;

      h3 {
        margin: 0;
        font-size: 1.2rem;
        font-weight: 600;
        color: #09283f;
      }

      .close-filters {
        display: none;
        background: none;
        border: none;
        color: #666;
        cursor: pointer;
        padding: 8px;
        border-radius: 4px;
        transition: all 0.3s ease;

        &:hover {
          color: #ee800a;
          background: rgba(238, 128, 10, 0.1);
        }
      }
    }
  }

  .filter-group {
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e0e0e0;

    &:last-child {
      border-bottom: none;
    }

    h4 {
      margin: 0 0 0.75rem;
      font-size: 1rem;
      font-weight: 600;
      color: #09283f;
    }
  }

  .search-box {
    position: relative;

    input {
      width: 100%;
      padding: 0.75rem 2.5rem 0.75rem 1rem;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      font-size: 0.9rem;
      background: #fff;
      transition: border-color 0.3s ease;

      &:focus {
        border-color: #ee800a;
        outline: none;
      }
    }

    .search-icon {
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      pointer-events: none;
    }
  }

  .filter-options {
    max-height: 200px;
    overflow-y: auto;
    padding-right: 0.5rem;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #ccc;
      border-radius: 20px;
    }
  }

  .filter-checkbox {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
    color: #4a4a4a;
    cursor: pointer;
    position: relative;
    padding-left: 28px;

    &:hover {
      color: #09283f;
    }

    input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }

    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 18px;
      width: 18px;
      background-color: #fff;
      border: 1px solid #ccc;
      border-radius: 4px;
      transition: all 0.3s ease;
    }

    input:checked ~ .checkmark {
      background-color: #ee800a;
      border-color: #ee800a;
    }

    .checkmark:after {
      content: "";
      position: absolute;
      display: none;
      left: 6px;
      top: 3px;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    input:checked ~ .checkmark:after {
      display: block;
    }
  }

  .range-filter {
    margin-top: 0.75rem;
  }

  .range-inputs {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    select {
      flex: 1;
      padding: 0.5rem;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      font-size: 0.9rem;
      background-color: white;
      transition: border-color 0.3s ease;

      &:focus {
        border-color: #ee800a;
        outline: none;
      }
    }

    span {
      color: #666;
      font-size: 0.9rem;
    }
  }

  .price-range {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
    color: #4a4a4a;

    .price-middle {
      color: #999;
    }
  }

  .slider-container {
    position: relative;
    height: 24px;
  }

  .price-slider {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    margin: 0;
    background: none;
    pointer-events: none;
    appearance: none;
    outline: none;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 16px;
      height: 16px;
      background: #ee800a;
      border-radius: 50%;
      cursor: pointer;
      pointer-events: auto;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      background: #ee800a;
      border-radius: 50%;
      cursor: pointer;
      pointer-events: auto;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &::-webkit-slider-runnable-track,
    &::-moz-range-track {
      height: 4px;
      background: #e0e0e0;
      border-radius: 2px;
    }
  }

  .filter-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e0e0e0;
  }

  .apply-filters {
    width: 100%;
    padding: 0.75rem;
    background: #ee800a;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.3s ease;

    &:hover {
      background: #d6700a;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(238, 128, 10, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .reset-filters {
    width: 100%;
    padding: 0.75rem;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #09283f;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.3s ease;

    &:hover {
      background: #f0f0f0;
      border-color: #ccc;
    }
  }

  .cars-content {
    flex-grow: 1;
    min-width: 0;
  }

  .mobile-filter-toggle {
    display: none;
    align-items: center;
    gap: 0.5rem;
    background: #fff;
    color: #09283f;
    border: 1px solid #e0e0e0;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    margin-bottom: 1.5rem;
    transition: all 0.3s ease;

    &:hover {
      background: #ee800a;
      color: white;
      border-color: #ee800a;
    }
  }

  .results-summary {
    margin-bottom: 1.5rem;

    p {
      font-size: 0.9rem;
      color: #666;
      margin: 0;
    }
  }

  .car-results {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }

  .car-card {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.4s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    position: relative;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);

      .car-image {
        transform: scale(1.05);
      }

      .car-name {
        color: #ee800a;
      }
    }
  }

  .car-image-wrapper {
    width: 100%;
    height: 200px;
    overflow: hidden;
    position: relative;
    background: #f5f5f5;
  }

  .car-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.6s ease;
  }

  .lease-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background: #ee800a;
    color: white;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 2;
    box-shadow: 0 2px 8px rgba(238, 128, 10, 0.3);
  }

  .car-card-content {
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .car-name {
    font-size: 1.15rem;
    font-weight: 700;
    color: #09283f;
    margin: 0 0 1rem 0;
    transition: color 0.3s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .lease-pricing {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    .price-option {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      .price-label {
        font-size: 0.7rem;
        color: #999;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 500;
      }

      .price-value {
        font-size: 1.25rem;
        color: #ee800a;
        font-weight: 700;
      }
    }
  }

  .car-details {
    color: #666;
    font-size: 0.9rem;
    margin: 0 0 0.75rem 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .lease-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    color: #666;
    margin-top: auto;

    svg {
      flex-shrink: 0;
      color: #ee800a;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;

    .spinner {
      animation: spin 1s linear infinite;
      color: #ee800a;
    }
  }

  .no-results {
    margin-top: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;

    .no-results-container {
      background: #f9f9f9;
      border-radius: 12px;
      padding: 3rem 2rem;
      text-align: center;
      max-width: 500px;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
      border: 1px solid #e0e0e0;
    }

    .no-results-icon {
      color: #ee800a;
      margin-bottom: 1.5rem;
      opacity: 0.8;
    }

    .no-results-title {
      font-size: 1.5rem;
      font-weight: 600;
      color: #09283f;
      margin: 0 0 1rem 0;
    }

    .no-results-text {
      font-size: 1rem;
      color: #666;
      line-height: 1.6;
      margin: 0;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1200px) {
    .car-results {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 992px) {
    padding: 80px 0;

    .cars-container {
      flex-direction: column;
    }

    .filters-sidebar {
      width: 100%;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 100vh;
      z-index: 10000;
      background: #fff;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      padding: 0;
      border-radius: 0;
      overflow: hidden;
      max-height: 100vh;

      &.active {
        transform: translateX(0);
      }

      .filters-header {
        position: sticky;
        top: 0;
        background: #f9f9f9;
        padding: 1.5rem;
        margin: 0;
        z-index: 10001;
        border-bottom: 1px solid #e0e0e0;

        .close-filters {
          display: block;
        }
      }

      .filters-content {
        height: calc(100vh - 80px);
        overflow-y: auto;
        padding: 1.5rem;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background-color: #ccc;
          border-radius: 20px;
        }
      }
    }

    .mobile-filter-toggle {
      display: flex;
    }

    .section-header {
      .section-title {
        font-size: 2.8rem;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 60px 0;

    .car-results {
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }

    .section-header {
      margin-bottom: 2rem;

      .section-title {
        font-size: 2.2rem;
      }

      .pre-title {
        font-size: 0.8rem;
      }

      .section-tagline {
        font-size: 1.1rem;
      }
    }

    .container {
      padding: 0 1.5rem;
    }

    .car-image-wrapper {
      height: 160px;
    }

    .car-card-content {
      padding: 1rem;
    }

    .car-name {
      font-size: 1rem;
      margin-bottom: 0.75rem;
    }

    .lease-pricing {
      gap: 1rem;
      margin-bottom: 0.75rem;

      .price-option {
        .price-label {
          font-size: 0.65rem;
        }

        .price-value {
          font-size: 1.1rem;
        }
      }
    }

    .car-details {
      font-size: 0.85rem;
      margin-bottom: 0.5rem;
    }

    .lease-info {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 576px) {
    padding: 50px 0;

    .car-results {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .car-image-wrapper {
      height: 200px;
    }

    .section-header {
      .section-title {
        font-size: 1.8rem;
      }

      .section-tagline {
        font-size: 1rem;
      }
    }

    .container {
      padding: 0 1rem;
    }

    .results-summary {
      margin-bottom: 1rem;

      p {
        font-size: 0.85rem;
      }
    }
  }
}

:global(body.filters-open) {
  overflow: hidden;
}
</style>
